/**
 * Workshop showcase page for the event + agenda frames.
 *
 * Renders both frames with realistic mock data plus edge cases
 * (no status pill, streaming, error, empty agenda). Item clicks
 * only report into a local log — they execute nothing.
 */

import { useState } from "react";
import { EventFrame } from "@/workshop/components/shapes/event-frame/event-frame";
import { AgendaFrame } from "@/workshop/components/shapes/agenda-frame/agenda-frame";

const MOCK_AGENDA_ITEMS = [
  {
    id: "e1",
    time: "09:00",
    title: "Standup",
    detail: "Team call · 15 min",
  },
  {
    id: "e2",
    time: "12:00",
    title: "Lunch with Priya",
    detail: "Kreuzberg · 1h",
  },
  {
    id: "e3",
    time: "14:30",
    title: "Keynote — Designing with AI",
    detail: "Config, Berlin · Main stage",
    current: true,
  },
  {
    id: "e4",
    time: "16:00",
    title: "Design systems crit",
    detail: "Studio call · 45 min",
  },
  {
    id: "e5",
    time: "18:30",
    title: "Dinner with Config crew",
    detail: "Markthalle Neun · 2h",
  },
];

const LONG_AGENDA_ITEMS = [
  ...MOCK_AGENDA_ITEMS,
  {
    id: "e6",
    time: "20:00",
    title: "Late keynote replay with an extremely long title that must truncate gracefully",
    detail: "Hotel lobby stream · 1h · bring headphones and a charger for the long evening session",
  },
];

function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div className="mb-10" data-testid={testId}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

export function EventAgendaFramesPage() {
  const [lastEvent, setLastEvent] = useState<string | null>(null);

  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="event-agenda-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Event + Agenda Frames</h1>
        <p className="mt-1 text-sm text-white/40">
          iOS-style event row and day agenda. Click an agenda item
          to see it land in the log.
        </p>
        <div
          className="mt-3 flex min-h-8 flex-wrap items-center gap-2"
          data-testid="event-agenda-frames-event-log"
          aria-live="polite"
        >
          {lastEvent ? (
            <span
              className="rounded-pill bg-white/[0.08] px-2 py-1 text-xs text-white/70"
              data-testid="event-agenda-frames-last-event"
            >
              Event: {lastEvent}
            </span>
          ) : (
            <span className="text-xs text-white/30">
              No agenda items clicked yet.
            </span>
          )}
        </div>
      </div>

      <Section title="Event — upcoming" testId="event-agenda-frames-event-done">
        <EventFrame
          data-testid="event-agenda-frames-event"
        />
        <EventFrame
          month="NOV"
          day="03"
          title="Design systems crit with a very long title that truncates"
          detail="Monday · 16:00 – 16:45 · Studio call, remote link in invite"
          data-testid="event-agenda-frames-event-alt"
        />
      </Section>

      <Section title="Event — edge cases" testId="event-agenda-frames-event-edge">
        <EventFrame
          status="streaming"
          data-testid="event-agenda-frames-event-streaming"
        />
        <EventFrame
          status="error"
          data-testid="event-agenda-frames-event-error"
        />
        <EventFrame
          title=""
          detail=""
          data-testid="event-agenda-frames-event-empty"
        />
      </Section>

      <Section title="Agenda — today" testId="event-agenda-frames-agenda-done">
        <AgendaFrame
          data-testid="event-agenda-frames-agenda"
          onItemClick={setLastEvent}
        />
        <AgendaFrame
          date="Thursday, Oct 23"
          items={LONG_AGENDA_ITEMS}
          data-testid="event-agenda-frames-agenda-many"
          onItemClick={setLastEvent}
        />
      </Section>

      <Section title="Agenda — edge cases" testId="event-agenda-frames-agenda-edge">
        <AgendaFrame
          statusPill=""
          items={MOCK_AGENDA_ITEMS.slice(0, 2)}
          data-testid="event-agenda-frames-agenda-no-pill"
          onItemClick={setLastEvent}
        />
        <AgendaFrame
          status="streaming"
          data-testid="event-agenda-frames-agenda-streaming"
        />
        <AgendaFrame
          status="error"
          data-testid="event-agenda-frames-agenda-error"
        />
        <AgendaFrame
          items={[]}
          data-testid="event-agenda-frames-agenda-empty"
        />
      </Section>
    </div>
  );
}
